import React, { useMemo } from 'react';
// TODO: Import @material-ui from design-system folder
// eslint-disable-next-line restrict-imports/restrict-imports
import { ThemeProvider } from '@material-ui/core/styles';
import RemotasksUITheme from 'design-system/theme';
import Spinner from 'design-system/Spinner';

import { TaskType, GDCTaskApplication } from '@scale/shared-types';
import { AttachmentDimensions, IGraderParams } from '@scale/shared/grader/types';
import {
  AudioTranscriptionV2Task,
  CategorizationV2Task,
  CategorizationTask,
  CategorizationTaskReview,
  CategorizationCreateBenchmark,
  CategorizationBenchmarkFeedback,
  ComparisonTask,
  DataCollectionTask,
  GenericDataCollectionTask,
  ImageAnnotationTask,
  VideoPlaybackAnnotationTask,
  VideoAnnotationTask,
  SegmentAnnotationTask,
  TranscriptionTask,
  NamedEntityRecognitionTask,
  TextCollectionCreateBenchmark,
  TextCollectionTaskReview,
  TextCollectionTask,
  DocumentTranscriptionReview,
  DocumentTranscriptionCreateBenchmark,
  DocumentModelReview,
  DocumentTranscriptionAudit,
  DocumentModelAudit,
  MultiModalTranscriptionTask,
  DocumentTranscriptionTask,
  ChatTask,
  MultiChatTask,
  ChatV2,
  SweAgentTask,
  CruiseControl,
} from 'react-scale/components/Task/DynamicTaskComponent';
import FrontierDataTaskPreview from 'react-scale/components/scalers/tasks/FrontierData/FrontierDataTaskPreview';
import TextCollectionAnswerKey from 'react-scale/components/scalers/tasks/textcollection/TextCollectionAnswerKey';
import { AutosaveHook } from 'react-scale/hooks/useAutosave';
import getTypeDisplayName from '@scale/shared/get-type-display-name';
import type { TextCollectionParams } from '@scale/shared/types/params/textcollection';
import { LintResultsMap } from '@scale/shared/types/linter';
import { AuditPerAnnotation } from '@scale/shared/types/props/audit';
import { AnnotationAttributes } from '@scale/shared/types/annotation';
import { PrepopulatedBreakdownColors } from '@scale/shared/annotation';
import { AssignmentJson } from '@scale/shared/assignmentJson/types/AssignmentJson';
import { Params } from '@scale/shared/types/params';
import { CategorizationParamsV2 } from '@scale/shared/types/params/categorization';
import { AuditHeaderFooterInfo } from '../audits/CustomerAudit/types';
import AudioCollectionPlayback from 'react-scale/corp/views/demos/AudioCollectionAlpha/AudioCollectionPlayback';
import { AudioCollectionEncodedResponse } from '@scale/shared/types/response/audiocollection';
import { DocumentDiffState } from '@scale/shared/grader/problemZones/generateProblemZonesDocument';
import { ModelResultsDetail } from '@scale/shared/types/response/documentmodel';
import { FrontierDataTaskParams } from '@scale/shared/types/frontierData';
import {
  DocumentModelField,
  DocumentModelInternalTaskParams,
} from '@scale/shared/types/params/documentmodel';
import { UseLintingHook } from '../../hooks/useLinting';
import { TextCollectionResponse } from '@scale/shared/types/response/textcollection';
import {
  getLidarLiteLink,
  getVideoCaptioningLink,
  getDataCollectionV2Link,
} from '@scale/frontend-lib/helpers/redirectHelpers';
import { ICompleteReviewAssignmentArgs } from 'react-scale/redux/actions';
import { NoSsr } from '@mui/material';

export interface TaskProps {
  _id?: string;
  assignment?: AssignmentJson; // TODO make the assignment field required
  scale_internal_attachment?: string;
  scale_internal_attachments?: string[];
  customDivHeight?: string;
  params: Params;
  subtasks: { attempts: { response: any }[] }[];
  isReviewMode?: boolean;
  prepopulatedBreakdownColors?: PrepopulatedBreakdownColors;
  prepopulatedAttributeDefinitions?: AnnotationAttributes;
  isCreatingScenario?: boolean;
  isAnswerKey?: boolean;
  readOnly?: boolean;
  isCustomer?: boolean;
  taskRef?: React.Ref<any>;
  multiTaskResponse?: boolean;
  fixResponse?: $FIXME;
  taskerResponseInFeedback?: $FIXME;
  graderResponse?: $FIXME;
  graderDiffResponse?: $FIXME;
  lintResults?: LintResultsMap<any>;
  isFrameRejected?: (frame: $FIXME) => boolean;
  standalone?: boolean;
  handleResponseSelect?: $FIXME;
  initializeInAdvancedView?: boolean;
  isPreviewMode?: boolean;
  showHeader?: boolean;
  config?: $FIXME;
  auditCorruptions?: boolean;
  previousGraderParams?: IGraderParams;
  hideLabels?: boolean;
  type?: TaskType;
  forceLoadFromSnapshot?: boolean;
  cuboidPathSceneName?: string;
  handleIdSelect?: (x: string | null) => void;
  auditPerAnnotation?: AuditPerAnnotation;
  ocrTextUrls?: string[];
  // isCustomer only determines to show audit view or not. This is if a real customer is auditing vs internal.
  isRealCustomerAudit?: boolean;

  // Don't need this after CustomerAuditDeprecation migration
  auditHeaderFooterInfo?: AuditHeaderFooterInfo;

  isTaxonomyDisplay?: boolean;
  benchmarkPage?: boolean;

  // Used in VideoTask
  isSpeedAudit?: boolean;

  // Used in multi-task response, for diffing
  initialFrame?: number;
  setInitialFrame?: (initialFrame: number) => void;

  // Used for MultiModalTranscription view persistance
  showNER?: boolean;
  setShowNER?: (value: boolean) => void;

  // Used for DocumentTranscription diffing
  diffState?: DocumentDiffState;

  // Used for params coming from Document models
  internal_task_params?: DocumentModelInternalTaskParams;
  documentModelFields?: DocumentModelField[];

  // Used for DocumentModel tasks
  model_results_detail?: ModelResultsDetail;
  customer_format_response?: any;

  // Page dimensions for document tasks.
  document_pages?: AttachmentDimensions[];

  // == Start linter panel props sent through assignment wrappers
  // Currently only leveraged for customer audit, since it renders using LegacyTaskComponent
  taskErrors?: React.ReactNode;
  showingErrors?: boolean;
  toggleShowingErrors?: () => void;
  // == End linter panel props

  // TODO: Remove this hack.
  isModelDemoTextLines?: boolean;
  editable?: boolean;
  isLabelTool?: boolean;

  // for Scale Studio
  isScaleStudio?: boolean;

  // for workquests
  isWorkquests?: boolean;

  // for audit tool
  isAuditMode?: boolean;
  auditId?: string;
  initialResponse?: Partial<TextCollectionResponse>;
  customFunction?: any;

  // for sme audit tool
  onSubmit?: (args: ICompleteReviewAssignmentArgs) => Promise<void>;
  fixable?: boolean;
  hideHeader?: boolean;
  canReturnToQueue?: boolean;
  setAutosaveRef?: (ref: AutosaveHook<any> & { discardAutosaveWithPrompt: () => void }) => void;
  attemptId?: string;

  // chat task
  collapsibleInstruction?: boolean;
  taskComponentClassName?: string;

  // Used for calibration
  isCalibration?: boolean;

  // Experimental
  instructionUrl?: string;
}

interface ITaskComponentProps {
  assignment?: AssignmentJson;
  readOnly: boolean;
  taskType: TaskType;
  responseKey?: string;
  attemptNum?: number;
  taskProps: TaskProps;
  taskRef?: React.Ref<any>;
  compact?: boolean;
  standalone?: boolean;
  isTaxonomyDisplay?: boolean;
  useMultiView?: boolean;
  multiViewIndex?: number;
  setMultiViewIndex?: (multiViewIndex: number) => void;
  canSwitchMultiModal?: boolean;
  hideLeftSidebar?: boolean;
  lintingHook?: UseLintingHook;
  // hide the right hand panel for a text collection task / preview
  hideTextCollectionForm?: boolean;
  key?: any;
  isDocumentAIGo?: boolean;
  showResponseJson?: boolean;
}

const TaskComponent = (props: ITaskComponentProps) => {
  const taskProps = useMemo(() => {
    const newTaskProps: TaskProps = { ...props.taskProps };

    if (newTaskProps.scale_internal_attachment) {
      newTaskProps.params.attachment = newTaskProps.scale_internal_attachment;
    }

    if (newTaskProps.scale_internal_attachments) {
      newTaskProps.params.attachments = newTaskProps.scale_internal_attachments;
    }

    return newTaskProps;
  }, [props]);

  return (
    // The reason we need the theme context here is for when tasks are
    // being rendered under customer dashboard and it needs remotasks ui
    // theme variables, even though it is technically under the root of
    // customer dashboard.
    <ThemeProvider theme={RemotasksUITheme}>
      <React.Suspense fallback={<Spinner style={{ paddingBottom: '16px' }} />}>
        {/* eslint-disable-next-line complexity */}
        {(() => {
          switch (props.taskType) {
            case TaskType.AudioTranscription:
              // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
              return <AudioTranscriptionV2Task {...taskProps} ref={props.taskRef} />;
            case TaskType.Categorization:
              if ((taskProps.params as any).binarySceneUrl) {
                return <FakeSensorFusionTask {...taskProps} key={props.responseKey} />;
              }
              if ((taskProps.params as CategorizationParamsV2).taxonomies) {
                if (
                  (taskProps.multiTaskResponse || props.isTaxonomyDisplay) &&
                  !taskProps.benchmarkPage
                ) {
                  return (
                    <CategorizationBenchmarkFeedback
                      {...taskProps}
                      ref={props.taskRef}
                      // @ts-ignore
                      shouldShowFeedback={taskProps.isAnswerKey}
                    />
                  );
                }
                if (taskProps.isReviewMode && !taskProps.benchmarkPage) {
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  return <CategorizationTaskReview {...taskProps} ref={props.taskRef} />;
                }

                if (props.responseKey && !taskProps.benchmarkPage) {
                  return (
                    // @ts-ignore
                    <CategorizationCreateBenchmark
                      {...taskProps}
                      ref={props.taskRef}
                      key={props.responseKey}
                    />
                  );
                }

                return (
                  <CategorizationV2Task
                    {...taskProps}
                    key={props.taskProps._id}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    compact={props.compact}
                  />
                );
              }

              // Legacy task, to be deprecated
              return (
                <CategorizationTask
                  {...taskProps}
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  ref={props.taskRef}
                  responseKey={props.responseKey}
                />
              );
            case TaskType.Comparison:
              // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
              return <ComparisonTask {...taskProps} ref={props.taskRef} />;
            case TaskType.DataCollection:
              // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
              return <DataCollectionTask {...taskProps} ref={props.taskRef} />;
            case TaskType.GenericDataCollection:
              // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
              return <GenericDataCollectionTask {...taskProps} ref={props.taskRef} />;
            case TaskType.ImageAnnotation:
            case TaskType.PointAnnotation:
            case TaskType.LineAnnotation:
            case TaskType.Annotation:
            case TaskType.CuboidAnnotation:
            case TaskType.PolygonAnnotation:
              return (
                <ImageAnnotationTask
                  {...taskProps}
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  ref={props.taskRef}
                  key={props.taskProps._id}
                  responseKey={props.responseKey}
                  useMultiView={props.useMultiView}
                  multiViewIndex={props.multiViewIndex}
                  setMultiViewIndex={props.setMultiViewIndex}
                  canSwitchMultiModal={props.canSwitchMultiModal}
                />
              );
            case TaskType.VideoAnnotation:
            case TaskType.VideoBoxAnnotation:
            case TaskType.VideoCuboidAnnotation:
            case TaskType.VideoPolygonAnnotation:
              // @ts-ignore
              return (
                // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                <VideoAnnotationTask {...taskProps} ref={props.taskRef} key={props.responseKey} />
              );

            case TaskType.VideoPlaybackAnnotation:
              // @ts-ignore
              return (
                <VideoPlaybackAnnotationTask
                  {...taskProps}
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  ref={props.taskRef}
                  key={props.responseKey}
                  responseKey={props.responseKey}
                  useMultiView={props.useMultiView}
                  setMultiViewIndex={props.setMultiViewIndex}
                  canSwitchMultiModal={props.canSwitchMultiModal}
                />
              );
            case TaskType.SegmentAnnotation:
              return (
                // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                <SegmentAnnotationTask {...taskProps} ref={props.taskRef} key={props.responseKey} />
              );
            case TaskType.Transcription:
              // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
              return <TranscriptionTask {...taskProps} ref={props.taskRef} />;
            case TaskType.NamedEntityRecognition:
              if (props.isTaxonomyDisplay) {
                return (
                  <NamedEntityRecognitionTask
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    responseKey={props.responseKey}
                    showFooter={false}
                    compact={props.compact}
                  />
                );
              }
              return (
                <NamedEntityRecognitionTask
                  {...taskProps}
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  ref={props.taskRef}
                  responseKey={props.responseKey}
                  compact={props.compact}
                />
              );
            case TaskType.TextCollection: {
              // todo: response should not be embedded, need to fix this
              taskProps.fixResponse = taskProps.fixResponse?.response || taskProps.fixResponse;
              if (props.isTaxonomyDisplay) {
                return (
                  // @ts-ignore
                  <TextCollectionCreateBenchmark
                    {...taskProps}
                    ref={props.taskRef}
                    key={props.responseKey}
                    responseKey={'taxonomyDisplay'}
                    hideTextCollectionForm={props.hideTextCollectionForm}
                    showResponseJson={props.showResponseJson}
                  />
                );
              } else if (taskProps.graderDiffResponse) {
                const textParams = {
                  attachments: taskProps.params.attachments,
                  fields: (taskProps.params as TextCollectionParams).fields,
                };
                return (
                  <TextCollectionAnswerKey
                    params={textParams as TextCollectionParams}
                    responseKey={props.responseKey}
                    graderDiffResponse={taskProps.graderDiffResponse}
                  />
                );
              } else if (taskProps.isReviewMode) {
                return (
                  <TextCollectionTaskReview
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    key={props.responseKey}
                    responseKey={props.responseKey}
                    useMultiView={props.useMultiView}
                    setMultiViewIndex={props.setMultiViewIndex}
                    canSwitchMultiModal={props.canSwitchMultiModal}
                  />
                );
              } else if (props.responseKey) {
                return (
                  // @ts-ignore
                  <TextCollectionCreateBenchmark
                    {...taskProps}
                    ref={props.taskRef}
                    key={props.responseKey}
                    responseKey={props.responseKey}
                    showResponseJson={props.showResponseJson}
                  />
                );
              } else if (
                taskProps.isScaleStudio ||
                taskProps.isAuditMode ||
                taskProps.isWorkquests
              ) {
                return (
                  <TextCollectionTask
                    {...taskProps}
                    // @ts-expect-error @types/react@18 $FIXME
                    projectConfig={taskProps.assignment?.config}
                    ref={props.taskRef}
                    key={props.responseKey}
                    responseKey={props.responseKey}
                  />
                );
              }
              break;
            }
            case TaskType.DocumentTranscription:
              if (taskProps.isReviewMode && !taskProps.isCustomer) {
                return (
                  <DocumentTranscriptionReview
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    responseKey={props.responseKey}
                    key={props.responseKey}
                  />
                );
              } else if (taskProps.isCustomer || taskProps.isTaxonomyDisplay) {
                return (
                  <DocumentTranscriptionAudit
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    responseKey={props.responseKey}
                    key={props.responseKey}
                    isDocumentAIGo={props.isDocumentAIGo}
                  />
                );
              } else if (props.responseKey) {
                return (
                  <DocumentTranscriptionCreateBenchmark
                    {...taskProps}
                    ref={props.taskRef}
                    responseKey={props.responseKey}
                    // @ts-ignore
                    attemptNum={props.attemptNum}
                  />
                );
              } else if (taskProps.isScaleStudio) {
                return (
                  <DocumentTranscriptionTask
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    key={props.responseKey}
                    responseKey={props.responseKey}
                  />
                );
              }

              break;
            case TaskType.DocumentModel: {
              const { internal_task_params: { resultOutputEnabled = false } = {} } = taskProps;
              if (resultOutputEnabled) {
                return (
                  <DocumentModelAudit
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    responseKey={props.responseKey}
                    key={props.responseKey}
                    isModelAudit
                  />
                );
              }
              if (taskProps.isCustomer) {
                return (
                  <DocumentTranscriptionAudit
                    {...taskProps}
                    // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                    ref={props.taskRef}
                    responseKey={props.responseKey}
                    key={props.responseKey}
                    isModelAudit
                  />
                );
              }
              return (
                <DocumentModelReview
                  {...taskProps}
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  ref={props.taskRef}
                  responseKey={props.responseKey}
                  key={props.responseKey}
                />
              );
            }
            case TaskType.LidarAnnotation:
            case TaskType.LidarSegmentation:
            case TaskType.MultiStage:
            case TaskType.SensorFusion:
              return <FakeSensorFusionTask {...taskProps} key={props.responseKey} />;
            case TaskType.VideoCaptioning:
              return <FakeVideoCaptioningTask {...taskProps} key={props.responseKey} />;
            case TaskType.AudioCollection: {
              // TODO: eventually move all AudioCollection task files to components/scalers/tasks
              const audioStrData = (taskProps.fixResponse as AudioCollectionEncodedResponse)
                ?.audioData;
              return (
                <AudioCollectionPlayback
                  utterance={taskProps.params.attachment}
                  audioData={audioStrData}
                />
              );
            }
            case TaskType.MultiModalTranscription:
              return (
                <MultiModalTranscriptionTask
                  {...taskProps}
                  // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                  ref={props.taskRef}
                  key={props.responseKey}
                  responseKey={props.responseKey}
                />
              );
            case TaskType.Chat:
              return (
                <ChatTask {...taskProps} ref={props.taskRef} responseKey={props.responseKey} />
              );
            case TaskType.ChatExperimental:
            case TaskType.ChatV2:
              return (
                <NoSsr>
                  <ChatV2 {...taskProps} ref={props.taskRef} responseKey={props.responseKey} />
                </NoSsr>
              );
            case TaskType.MultiChat:
              return (
                // @ts-expect-error @types/react@18 - $FIXME: ref isn't typechecking here
                <MultiChatTask {...taskProps} ref={props.taskRef} responseKey={props.responseKey} />
              );
            case TaskType.SweAgent:
              return <SweAgentTask {...taskProps} ref={props.taskRef} />;
            case TaskType.DataCollectionV2: {
              if ((taskProps?.params as any)?.application === GDCTaskApplication.LidarLite) {
                return <FakeSensorFusionTask {...taskProps} />;
              }
              return <FakeDataCollectionV2Task {...taskProps} ref={props.taskRef} />;
            }
            case TaskType.FrontierData: {
              return (
                <FrontierDataTaskPreview
                  params={taskProps.params as unknown as FrontierDataTaskParams}
                  previewParams={{ type: 'task', taskId: taskProps._id! }}
                />
              );
            }
            case TaskType.CruiseControl:
              return <CruiseControl {...taskProps} ref={props.taskRef} />;
            default: {
              const typeDisplayName = getTypeDisplayName(props.taskType, {
                workerFacing: !taskProps.isCustomer,
              });
              return (
                <div style={{ padding: '16px' }}>
                  No details available for <strong>{typeDisplayName}</strong> tasks.
                </div>
              );
            }
          }
        })()}
      </React.Suspense>
    </ThemeProvider>
  );
};

TaskComponent.defaultProps = {
  readOnly: true,
};

export default TaskComponent;

function FakeSensorFusionTask(props: any) {
  React.useEffect(() => {
    let url = '';

    const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());

    // for customer audit
    // Afraid to swap the order here, but I would imagine we should first check the assignment and fall back on the task_id
    // For now added subtask assignment check
    if (props.task_id && props.assignmentType !== 'subtask') {
      url = getLidarLiteLink({
        ...params,
        taskId: props.task_id,
      });
    }
    // for other flows
    else if (props.assignment && props.assignment.assignmentId) {
      url = getLidarLiteLink({ ...params, sceneId: props.assignment.assignmentId });
    }

    if (url) {
      window.location.href = url;
    }
  }, []);

  if (props.currentAuditWorkId) {
    const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
    const url = getLidarLiteLink({ ...params, sceneId: props.currentAuditWorkId });

    return (
      <a href={url} target="_blank" rel="noopener noreferrer">
        Open work in a new tab
      </a>
    );
  }
  return <div>Loading lidar lite...</div>;
}
function FakeDataCollectionV2Task(props: any) {
  let url = '';
  const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());

  if (params.taskId) {
    url = getDataCollectionV2Link({
      ...params,
      ...(props.assignment?.isAudit ? { mode: 'audit' } : {}),
    });
  }

  if (url) {
    window.location.href = url;
  }

  return <div>Loading data collection v2 tool...</div>;
}

function FakeVideoCaptioningTask(props: any) {
  React.useEffect(() => {
    const params = Object.fromEntries(new URLSearchParams(window.location.search).entries());
    const url = getVideoCaptioningLink({ ...params, taskId: props.task_id });
    if (url) {
      window.location.href = url;
    }
  }, []);

  return <div>Loading video captioning tool...</div>;
}
